import { useCallback, useEffect, useMemo, useState } from "react";
import { Save, ShieldAlert, ShieldCheck, ShieldOff } from "lucide-react";

import {
  DataProtectionService,
  type InjectionAction,
  type LegacyPolicyOut,
  type PiiAction,
  type UpdateDataProtectionPayload,
  type CreateTenantDefaultPolicyPayload,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatRelativeTimeID } from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Section } from "@/components/shared/section";
import { cn } from "@/lib/utils";

const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);

const TENANT_DEFAULT_NAME = "tenant-default";
const DEFAULT_PII: PiiAction = "partial_mask";
const DEFAULT_INJECTION: InjectionAction = "warn";
const DEFAULT_THRESHOLD = 0.7;

// Ordered strictest → loosest for the dropdown so the most protective
// option reads first (Sesi ZTA-POLICY-FLOOR).
const PII_OPTIONS: PiiAction[] = [
  "block",
  "full_mask",
  "partial_mask",
  "warn",
  "allow",
];
const INJECTION_OPTIONS: InjectionAction[] = ["block", "warn", "allow"];

const PII_LABEL_KEY: Record<PiiAction, TranslationKey> = {
  block: "settings.dataProtection.pii.options.block.label",
  full_mask: "settings.dataProtection.pii.options.full_mask.label",
  partial_mask: "settings.dataProtection.pii.options.partial_mask.label",
  warn: "settings.dataProtection.pii.options.warn.label",
  allow: "settings.dataProtection.pii.options.allow.label",
};
const PII_DESC_KEY: Record<PiiAction, TranslationKey> = {
  block: "settings.dataProtection.pii.options.block.description",
  full_mask: "settings.dataProtection.pii.options.full_mask.description",
  partial_mask: "settings.dataProtection.pii.options.partial_mask.description",
  warn: "settings.dataProtection.pii.options.warn.description",
  allow: "settings.dataProtection.pii.options.allow.description",
};

const INJECTION_LABEL_KEY: Record<InjectionAction, TranslationKey> = {
  block: "settings.dataProtection.injection.options.block.label",
  warn: "settings.dataProtection.injection.options.warn.label",
  allow: "settings.dataProtection.injection.options.allow.label",
};
const INJECTION_DESC_KEY: Record<InjectionAction, TranslationKey> = {
  block: "settings.dataProtection.injection.options.block.description",
  warn: "settings.dataProtection.injection.options.warn.description",
  allow: "settings.dataProtection.injection.options.allow.description",
};

interface EnforcementBadgeShape {
  tone: BadgeTone;
  Icon: typeof ShieldCheck;
  labelKey: TranslationKey;
}

function piiBadge(action: PiiAction): EnforcementBadgeShape {
  switch (action) {
    case "block":
      return {
        tone: "danger",
        Icon: ShieldAlert,
        labelKey: "settings.dataProtection.badges.enforced",
      };
    case "full_mask":
    case "partial_mask":
      return {
        tone: "ocean",
        Icon: ShieldCheck,
        labelKey: "settings.dataProtection.badges.masking",
      };
    case "warn":
      return {
        tone: "warning",
        Icon: ShieldCheck,
        labelKey: "settings.dataProtection.badges.monitoring",
      };
    case "allow":
      return {
        tone: "neutral",
        Icon: ShieldOff,
        labelKey: "settings.dataProtection.badges.off",
      };
  }
}

function injectionBadge(action: InjectionAction): EnforcementBadgeShape {
  switch (action) {
    case "block":
      return {
        tone: "danger",
        Icon: ShieldAlert,
        labelKey: "settings.dataProtection.badges.enforced",
      };
    case "warn":
      return {
        tone: "warning",
        Icon: ShieldCheck,
        labelKey: "settings.dataProtection.badges.monitoring",
      };
    case "allow":
      return {
        tone: "neutral",
        Icon: ShieldOff,
        labelKey: "settings.dataProtection.badges.off",
      };
  }
}

/** Identify the tenant-default policy row out of the list.
 *  Convention (model docstring): ``user_id IS NULL`` + ``is_active``. */
function findTenantDefault(rows: LegacyPolicyOut[]): LegacyPolicyOut | null {
  return (
    rows.find((p) => p.user_id === null && p.is_active === true) ?? null
  );
}

function toThresholdNumber(raw: string | number): number {
  if (typeof raw === "number") return raw;
  const n = Number(raw);
  return Number.isFinite(n) ? n : DEFAULT_THRESHOLD;
}

export default function SettingsDataProtectionTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canEdit = user ? TENANT_ADMIN_ROLES.has(user.role) : false;

  const fetcher = useCallback(() => DataProtectionService.list(), []);
  const { data, error, isLoading, refetch } =
    useApi<LegacyPolicyOut[]>(fetcher);

  const existing = useMemo(
    () => (data ? findTenantDefault(data) : null),
    [data]
  );

  const [piiAction, setPiiAction] = useState<PiiAction>(DEFAULT_PII);
  const [injectionAction, setInjectionAction] =
    useState<InjectionAction>(DEFAULT_INJECTION);
  const [threshold, setThreshold] = useState<string>(
    String(DEFAULT_THRESHOLD)
  );
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Hydrate from server when a tenant-default policy already exists.
  // The wizard at /register doesn't create one — so a fresh tenant
  // lands here without a row and the editor starts on the documented
  // service defaults (MASK / WARN / 0.70).
  useEffect(() => {
    if (existing) {
      setPiiAction(existing.pii_action);
      setInjectionAction(existing.prompt_injection_action);
      setThreshold(String(toThresholdNumber(existing.prompt_injection_threshold)));
    }
  }, [existing?.id]);

  const update = useMutation<UpdateDataProtectionPayload, LegacyPolicyOut>(
    (payload) =>
      DataProtectionService.updateProtection(existing!.id, payload),
    {
      onSuccess: () => {
        setSavedAt(new Date());
        void refetch();
      },
    }
  );

  const create = useMutation<
    CreateTenantDefaultPolicyPayload,
    LegacyPolicyOut
  >((payload) => DataProtectionService.createTenantDefault(payload), {
    onSuccess: () => {
      setSavedAt(new Date());
      void refetch();
    },
  });

  const thresholdNumber = Number(threshold);
  const thresholdValid =
    Number.isFinite(thresholdNumber) &&
    thresholdNumber >= 0 &&
    thresholdNumber <= 1;

  // Round-trip safety on dirty check: compare numeric thresholds so a
  // server-supplied "0.7" doesn't read as dirty against the typed "0.7".
  const dirty = useMemo(() => {
    if (!existing) return true; // no row yet — save creates it.
    return (
      existing.pii_action !== piiAction ||
      existing.prompt_injection_action !== injectionAction ||
      Math.abs(
        toThresholdNumber(existing.prompt_injection_threshold) -
          thresholdNumber
      ) > 1e-6
    );
  }, [existing, piiAction, injectionAction, thresholdNumber]);

  const canSubmit = canEdit && dirty && thresholdValid && !isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (existing) {
      await update.mutate({
        pii_action: piiAction,
        prompt_injection_action: injectionAction,
        prompt_injection_threshold: thresholdNumber,
      });
    } else {
      await create.mutate({
        name: TENANT_DEFAULT_NAME,
        pii_action: piiAction,
        prompt_injection_action: injectionAction,
        prompt_injection_threshold: thresholdNumber,
      });
    }
  };

  const handleReset = () => {
    if (existing) {
      setPiiAction(existing.pii_action);
      setInjectionAction(existing.prompt_injection_action);
      setThreshold(
        String(toThresholdNumber(existing.prompt_injection_threshold))
      );
    } else {
      setPiiAction(DEFAULT_PII);
      setInjectionAction(DEFAULT_INJECTION);
      setThreshold(String(DEFAULT_THRESHOLD));
    }
    update.reset();
    create.reset();
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error && !data) {
    return <Alert variant="warning">{parseApiError(error)}</Alert>;
  }

  const mutationError = update.error ?? create.error;
  const isSubmitting = update.isLoading || create.isLoading;

  return (
    <div className="space-y-8">
      <Alert variant="info">
        {t("settings.dataProtection.overrideNotice")}
      </Alert>
      {!existing && (
        <Alert variant="info">
          {t("settings.dataProtection.firstSaveNotice")}
        </Alert>
      )}
      {!canEdit && (
        <Alert variant="info">
          {t("settings.dataProtection.readOnlyNotice")}
        </Alert>
      )}
      {mutationError && (
        <Alert variant="danger">{parseApiError(mutationError)}</Alert>
      )}
      {savedAt && !mutationError && (
        <Alert variant="success" role="status">
          {t("settings.dataProtection.savedNotice", {
            time: formatRelativeTimeID(savedAt),
          })}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <Section
          title={t("settings.dataProtection.pii.title")}
          description={t("settings.dataProtection.pii.description")}
        >
          <ActionGroup
            name="pii-action"
            options={PII_OPTIONS}
            value={piiAction}
            onChange={(v) => setPiiAction(v)}
            disabled={!canEdit}
            labelKey={(opt) => PII_LABEL_KEY[opt]}
            descKey={(opt) => PII_DESC_KEY[opt]}
            badge={piiBadge(piiAction)}
            badgeAriaLabelKey="settings.dataProtection.badges.aria"
          />
        </Section>

        <Section
          title={t("settings.dataProtection.injection.title")}
          description={t("settings.dataProtection.injection.description")}
        >
          <ActionGroup
            name="injection-action"
            options={INJECTION_OPTIONS}
            value={injectionAction}
            onChange={(v) => setInjectionAction(v)}
            disabled={!canEdit}
            labelKey={(opt) => INJECTION_LABEL_KEY[opt]}
            descKey={(opt) => INJECTION_DESC_KEY[opt]}
            badge={injectionBadge(injectionAction)}
            badgeAriaLabelKey="settings.dataProtection.badges.aria"
          />

          <div className="space-y-2 rounded-md border border-ocean-100 bg-white p-5">
            <label
              htmlFor="injection-threshold"
              className="block text-sm font-medium text-ocean-900"
            >
              {t("settings.dataProtection.injection.thresholdLabel")}
            </label>
            <div className="flex items-center gap-4">
              <input
                id="injection-threshold"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={thresholdNumber}
                onChange={(e) => setThreshold(e.target.value)}
                disabled={!canEdit}
                className="flex-1 accent-ocean-600"
              />
              <input
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                min={0}
                max={1}
                step={0.01}
                disabled={!canEdit}
                aria-invalid={!thresholdValid}
                className="h-10 w-24 rounded-md border border-ocean-100 px-2 text-sm text-ocean-900 focus:outline-none focus:ring-2 focus:ring-ocean-400"
              />
            </div>
            <p
              className={cn(
                "text-xs",
                thresholdValid ? "text-neutral-500" : "text-danger-600"
              )}
              role={thresholdValid ? undefined : "alert"}
            >
              {thresholdValid
                ? t("settings.dataProtection.injection.thresholdHint")
                : t("settings.dataProtection.injection.thresholdError")}
            </p>
          </div>
        </Section>

        {canEdit && (
          <div className="flex items-center justify-end gap-2 border-t border-ocean-100 pt-5">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              disabled={isSubmitting || !dirty}
            >
              {t("settings.dataProtection.resetButton")}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              <Save size={14} />
              {isSubmitting
                ? t("settings.dataProtection.saveButtonLoading")
                : t("settings.dataProtection.saveButton")}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

interface ActionGroupProps<T extends string> {
  /** Unique radio-group name. WITHOUT this the PII + injection radios
   *  would share state (browsers group by `name`) and selecting one
   *  PII option would silently deselect the injection choice. */
  name: string;
  options: ReadonlyArray<T>;
  value: T;
  onChange: (v: T) => void;
  disabled: boolean;
  labelKey: (opt: T) => TranslationKey;
  descKey: (opt: T) => TranslationKey;
  badge: EnforcementBadgeShape;
  badgeAriaLabelKey: TranslationKey;
}

function ActionGroup<T extends string>({
  name,
  options,
  value,
  onChange,
  disabled,
  labelKey,
  descKey,
  badge,
  badgeAriaLabelKey,
}: ActionGroupProps<T>) {
  const { t } = useTranslation();
  const BadgeIcon = badge.Icon;
  return (
    <div className="space-y-3">
      <div
        role="status"
        aria-label={t(badgeAriaLabelKey)}
        className="flex items-center justify-end"
      >
        <Badge tone={badge.tone}>
          <BadgeIcon size={12} />
          {t(badge.labelKey)}
        </Badge>
      </div>
      <div className="grid grid-cols-1 gap-2 tablet:grid-cols-2">
        {options.map((opt) => {
          const checked = value === opt;
          return (
            <label
              key={opt}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-md border p-4 transition-colors",
                disabled && "cursor-not-allowed opacity-60",
                checked
                  ? "border-ocean-400 bg-ocean-50/40"
                  : "border-ocean-100 bg-white hover:border-ocean-200"
              )}
            >
              <input
                type="radio"
                name={name}
                value={opt}
                checked={checked}
                disabled={disabled}
                onChange={() => onChange(opt)}
                className="mt-1 h-4 w-4 text-ocean-600 focus:ring-ocean-400"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ocean-900">
                  {t(labelKey(opt))}
                </div>
                <p className="mt-1 text-xs leading-relaxed text-neutral-600">
                  {t(descKey(opt))}
                </p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
